import { Phone, Navigation, Clock, MapPin } from "lucide-react";
import { HOURS, RESTAURANT } from "../data/site";
import { Reveal } from "./Reveal";

const parseTime = (t) => {
  // "12:00 PM" -> minutes since midnight
  const [time, mer] = t.split(" ");
  let [h, m] = time.split(":").map(Number);
  if (mer === "PM" && h !== 12) h += 12;
  if (mer === "AM" && h === 12) h = 0;
  return h * 60 + m;
};

export const HoursLocation = () => {
  const now = new Date();
  const today = now.getDay();
  const nowMins = now.getHours() * 60 + now.getMinutes();
  const todayRow = HOURS.find((h) => h.day === today);
  const openNow =
    todayRow?.open && nowMins >= parseTime(todayRow.open) && nowMins < parseTime(todayRow.close);

  return (
    <section id="hours" data-testid="hours-location" className="bg-beige py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-10 lg:gap-14 items-start">
        {/* Hours */}
        <div>
          <Reveal>
            <span className="font-heading text-gold-dark uppercase tracking-[0.3em] text-sm flex items-center gap-2">
              <Clock size={16} /> Hours
            </span>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-4 font-heading text-4xl md:text-5xl font-semibold text-charcoal">
              When We're <span className="font-script text-burgundy text-5xl md:text-6xl">Open</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div
              data-testid="open-status-banner"
              className={`mt-6 inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full font-body font-semibold text-sm ${
                openNow ? "bg-green-700 text-cream" : "bg-charcoal/10 text-charcoal"
              }`}
            >
              <span className={`w-2.5 h-2.5 rounded-full ${openNow ? "bg-green-300 animate-pulse" : "bg-charcoal/40"}`} />
              {openNow ? "Open Now" : "Currently Closed"}
            </div>
          </Reveal>

          <div className="mt-7 space-y-3">
            {HOURS.map((h, i) => {
              const isToday = h.day === today;
              return (
                <Reveal key={h.label} delay={0.03 * i}>
                  <div
                    data-testid={`hours-row-${h.label.toLowerCase()}`}
                    className={`flex items-center justify-between px-6 py-4 rounded-2xl border transition-all ${
                      isToday
                        ? "bg-burgundy text-cream border-burgundy shadow-[0_10px_30px_rgba(122,28,34,0.2)]"
                        : "bg-white border-beige"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className={`font-heading text-lg font-semibold ${isToday ? "text-cream" : "text-charcoal"}`}>
                        {h.label}
                      </span>
                      {isToday && (
                        <span className="font-body text-[0.65rem] uppercase tracking-widest bg-gold text-charcoal px-2 py-0.5 rounded-full">
                          Today
                        </span>
                      )}
                    </div>
                    <span className={`font-body ${h.open ? (isToday ? "text-cream" : "text-charcoal/80") : "text-red-500/80 font-medium"}`}>
                      {h.open ? `${h.open} – ${h.close}` : "Closed"}
                    </span>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>

        {/* Location */}
        <div id="location" data-testid="location-block">
          <Reveal>
            <span className="font-heading text-gold-dark uppercase tracking-[0.3em] text-sm flex items-center gap-2">
              <MapPin size={16} /> Location
            </span>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-4 font-heading text-4xl md:text-5xl font-semibold text-charcoal">
              Come <span className="font-script text-burgundy text-5xl md:text-6xl">Visit</span>
            </h2>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-7 bg-white rounded-3xl border border-beige overflow-hidden shadow-[0_4px_20px_rgba(122,28,34,0.05)]">
              <iframe
                title="Rainey's Soul Food location map"
                data-testid="location-map"
                src={RESTAURANT.mapEmbedUrl}
                width="100%"
                height="300"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
              <div className="p-7">
                <p className="font-heading text-2xl text-charcoal font-semibold">{RESTAURANT.name}</p>
                <p className="font-body text-charcoal/70 mt-1">
                  {RESTAURANT.address.line1}
                  <br />
                  {RESTAURANT.address.line2}
                </p>
                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  <a
                    data-testid="location-directions-btn"
                    href={RESTAURANT.directionsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-burgundy text-cream px-6 py-3.5 rounded-full font-body font-semibold tracking-wide hover:bg-burgundy-dark transition-all duration-300 hover:scale-[1.02]"
                  >
                    <Navigation size={17} /> Get Directions
                  </a>
                  <a
                    data-testid="location-call-btn"
                    href={RESTAURANT.phoneHref}
                    className="flex-1 inline-flex items-center justify-center gap-2 border border-gold text-charcoal px-6 py-3.5 rounded-full font-body font-semibold tracking-wide hover:bg-gold hover:text-cream transition-all duration-300"
                  >
                    <Phone size={17} /> Call Restaurant
                  </a>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
